import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBookmarks } from '../context/BookmarkContext';
import { useTeam } from '../context/TeamContext';
import { TYPE_ICONS } from '../constants';
import { togglePublic, deleteEntry, updateEntry, supabase } from '../lib/supabase';
import ReportModal from './ReportModal';
import ShareToTeam from './ShareToTeam';

const TYPE_TAG_CLASS = {
  image: 'tag-image', video: 'tag-video', text: 'tag-text',
  longform: 'tag-longform', code: 'tag-code', prompt: 'tag-prompt',
  distilled: 'tag-prompt', sop: 'tag-code',
};
const TYPE_LABELS = {
  image: 'Image', video: 'Video', text: 'Text',
  longform: 'Long Form', code: 'Code', prompt: 'Prompt',
  distilled: 'Summary', sop: 'SOP',
};

const PROMPT_COLLAPSE_LEN = 220;

const shareItemStyle = {
  padding: '10px 14px', fontSize: 13, color: 'var(--text)',
  cursor: 'pointer', borderBottom: '0.5px solid var(--border)',
  display: 'flex', alignItems: 'center', gap: 8,
  transition: 'background 0.1s',
};

function formatFullDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function sourceLabel(url) {
  try {
    return new URL(url).hostname.replace('www.', '');
  } catch {
    return url.slice(0, 50);
  }
}

// ── Call the interactions endpoint ───────────────────────────────────────────
async function postInteraction(action, entryId, sessionToken) {
  if (!sessionToken) { console.warn('[CPG] postInteraction: no session token'); return null; }
  if (!entryId)      { console.warn('[CPG] postInteraction: no entry_id'); return null; }
  try {
    const res = await fetch('/api/ai-usage?resource=interact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionToken}`,
      },
      body: JSON.stringify({ action, entry_id: entryId }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      console.warn('[CPG] postInteraction failed:', res.status, err);
      return null;
    }
    return await res.json();
  } catch (e) {
    console.error('[CPG] postInteraction error:', e);
    return null;
  }
}

export default function DetailPanel({ entry, onClose, onDeleted, onUpdated, folders }) {
  const { user, isAdmin } = useAuth();
  const { teams } = useTeam();
  const { isBookmarked: checkBookmarked, addBookmark, removeBookmark } = useBookmarks();
  const [showShareToast, setShowShareToast] = useState('');
  const [copied, setCopied]                 = useState(false);
  const [toggling, setToggling]             = useState(false);
  const [movingFolder, setMovingFolder]     = useState(false);
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [showReport, setShowReport]         = useState(false);
  const [showSaveAsSkill, setShowSaveAsSkill] = useState(false);
  const [showShareMenu, setShowShareMenu]   = useState(false);
  const [shareCopied, setShareCopied]       = useState(false);

  // ── Interaction state — counts from entry prop, bookmark from context ───────
  const [bookmarkCount, setBookmarkCount]   = useState(entry?.bookmark_count || 0);
  const [copyCount, setCopyCount]           = useState(entry?.copy_count     || 0);
  const [bookmarking, setBookmarking]       = useState(false);

  // ── Lazy load full entry content (prompt, text_output, notes) ────────────
  // The list view no longer fetches these heavy fields — we load on demand here.
  const [fullEntry, setFullEntry] = useState(entry);
  const [loadingFull, setLoadingFull] = useState(false);
  useEffect(() => {
    if (!entry?.id) return;
    // Reset to the lightweight entry immediately (shows title/meta right away)
    setFullEntry(entry);
    // If prompt is already present (e.g. opened from a full fetch), skip
    if (entry.prompt !== undefined || fullEntry.text_output !== undefined) return;
    setLoadingFull(true);
    import('../lib/supabase').then(({ fetchEntryById }) =>
      fetchEntryById(entry.id)
        .then(full => { if (full) setFullEntry(full); })
        .catch(console.error)
        .finally(() => setLoadingFull(false))
    );
  }, [entry?.id]);

  // Reset counts when a different entry is opened
  useEffect(() => {
    setBookmarkCount(entry?.bookmark_count || 0);
    setCopyCount(entry?.copy_count     || 0);
  }, [entry?.id]);

  // Early return AFTER all hooks
  if (!entry) return null;

  // Bookmark status always from context — correct across all entries, persists on reload
  const isBookmarked = checkBookmarked(entry.id);

  const isOwner        = user && entry.user_id === user.id;
  const canEdit        = isOwner || isAdmin;
  const canShareToTeam = isOwner && user;
  const canSeePrompt   = !!user;
  const canInteract    = !!user && !isOwner; // can't bookmark/interact with own entries

  const promptFull    = fullEntry.prompt || '';
  const needsTruncate = promptFull.length > PROMPT_COLLAPSE_LEN;
  const promptShown   = (needsTruncate && !promptExpanded)
    ? promptFull.slice(0, PROMPT_COLLAPSE_LEN).replace(/\s\S*$/, '') + '…'
    : promptFull;

  // ── Get session token (lazy — called only on user-initiated actions) ─────
  const getToken = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  };

  // ── Bookmark toggle ───────────────────────────────────────────────────────
  const handleBookmark = async () => {
    if (!canInteract || bookmarking) return;
    setBookmarking(true);
    const token  = await getToken();
    const action = isBookmarked ? 'unbookmark' : 'bookmark';
    const result = await postInteraction(action, entry.id, token);
    if (result?.ok) {
      // Update context — persists across all panels and page navigation
      if (action === 'bookmark') addBookmark(entry.id);
      else removeBookmark(entry.id);
      setBookmarkCount(result.bookmark_count ?? bookmarkCount);
    }
    setBookmarking(false);
  };

  // ── Copy prompt + log copy event ─────────────────────────────────────────
  const copyPrompt = async () => {
    navigator.clipboard?.writeText(fullEntry.prompt).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);

    // Log copy event — only on click, not on open
    if (user) {
      const token = await getToken();
      const result = await postInteraction('copy', entry.id, token);
      if (result?.ok) setCopyCount(result.copy_count ?? copyCount);
    }
  };

  const shareEntry = () => {
    if (!entry.is_public) {
      alert('Make this prompt public first to share a link.');
      setShowShareMenu(false);
      return;
    }
    const url = `${window.location.origin}/entry/${entry.id}`;
    navigator.clipboard?.writeText(url).catch(() => {});
    setShareCopied(true);
    setShowShareMenu(false);
    setTimeout(() => setShareCopied(false), 2000);
  };

  const shareNative = () => {
    const url = `${window.location.origin}/entry/${entry.id}`;
    if (navigator.share) {
      navigator.share({ title: entry.title, text: `Check out this AI prompt: ${entry.title}`, url }).catch(() => {});
    } else {
      shareEntry();
    }
    setShowShareMenu(false);
  };

  const handleTogglePublic = async () => {
    setToggling(true);
    try {
      await togglePublic(entry.id, !entry.is_public);
      onUpdated({ ...entry, is_public: !entry.is_public });
    } catch (e) { alert(e.message); }
    setToggling(false);
  };

  const handleMoveFolder = async (folderId) => {
    setMovingFolder(false);
    try {
      const updated = await updateEntry(entry.id, { folder_id: folderId || null });
      onUpdated({ ...entry, ...updated });
    } catch (e) { alert(e.message); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this entry? This cannot be undone.')) return;
    try {
      await deleteEntry(entry.id);
      onDeleted(entry.id);
    } catch (e) { alert(e.message); }
  };

  return (
    <div className="right-panel">
      <div className="panel-header">
        <span className="panel-title" style={{
          maxWidth: '80%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{entry.title}</span>
        <button className="panel-close" onClick={onClose}>×</button>
      </div>

      <div className="panel-body" onClick={() => setShowShareMenu(false)}>

        {/* ── Media preview ── */}
        {entry.file_url && entry.type === 'image' && (
          <div className="detail-preview" style={{ padding: 0 }}>
            <img src={entry.file_url} alt={entry.title}
              style={{ width: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 'var(--radius)', display: 'block' }} />
          </div>
        )}
        {entry.file_url && entry.type === 'video' && (
          <div className="detail-preview" style={{ padding: 0 }}>
            <video src={entry.file_url} controls playsInline
              style={{ width: '100%', maxHeight: 280, borderRadius: 'var(--radius)', display: 'block', background: '#000' }} />
          </div>
        )}
        {!entry.file_url && !['image', 'video'].includes(entry.type) && (
          <div className="detail-preview" style={{ padding: 20 }}>
            <span style={{ fontSize: 52 }}>{TYPE_ICONS[entry.type] || '📄'}</span>
          </div>
        )}
        {!entry.file_url && ['image', 'video'].includes(entry.type) && (
          <div className="detail-preview" style={{ padding: 20 }}>
            <span style={{ fontSize: 52 }}>{TYPE_ICONS[entry.type] || '📄'}</span>
          </div>
        )}

        {/* ── Interaction stats bar ── */}
        {entry.is_public && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '8px 0', borderBottom: '0.5px solid var(--border)',
            marginBottom: 4,
          }}>
            {/* Bookmark button */}
            {canInteract && (
              <button
                onClick={handleBookmark}
                disabled={bookmarking}
                title={isBookmarked ? 'Remove bookmark' : 'Bookmark this entry'}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  background: isBookmarked ? 'rgba(245,158,11,0.1)' : 'none',
                  border: `0.5px solid ${isBookmarked ? 'rgba(245,158,11,0.4)' : 'var(--border2)'}`,
                  borderRadius: 'var(--radius-sm)', padding: '5px 10px',
                  cursor: bookmarking ? 'wait' : 'pointer',
                  color: isBookmarked ? '#f59e0b' : 'var(--text-3)',
                  fontSize: 12, fontFamily: 'var(--font)',
                  transition: 'all 0.15s',
                }}>
                <svg width="13" height="13" viewBox="0 0 24 24"
                  fill={isBookmarked ? '#f59e0b' : 'none'}
                  stroke={isBookmarked ? '#f59e0b' : 'currentColor'} strokeWidth="2">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
                {bookmarkCount > 0 && <span>{bookmarkCount}</span>}
              </button>
            )}

            {/* Stats — copies + views (read-only for owners) */}
            {(isOwner || !canInteract) && bookmarkCount > 0 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-3)' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="2">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
                {bookmarkCount}
              </span>
            )}

            {copyCount > 0 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-3)' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
                {copyCount} {copyCount === 1 ? 'copy' : 'copies'}
              </span>
            )}


          </div>
        )}

        {/* ── Type + Model ── */}
        <div style={{ display: 'flex', gap: 12 }}>
          <div className="detail-section" style={{ flex: 1 }}>
            <div className="detail-label">Output type</div>
            <div><span className={`tag ${TYPE_TAG_CLASS[entry.type] || 'tag-prompt'}`}>{TYPE_LABELS[entry.type] || entry.type}</span></div>
          </div>
          <div className="detail-section" style={{ flex: 2 }}>
            <div className="detail-label">Model / Tool</div>
            <div className="detail-value mono">{entry.model || '—'}</div>
          </div>
        </div>

        {/* ── Source URL ── */}
        {entry.source_url && (
          <div className="detail-section">
            <div className="detail-label">Source</div>
            <a href={entry.source_url} target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 12.5, color: 'var(--accent)', textDecoration: 'none',
                display: 'inline-flex', alignItems: 'center', gap: 5, wordBreak: 'break-all' }}
              onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 3H3a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1V9"/>
                <path d="M13 1h2v2M9 7l6-6"/>
              </svg>
              {sourceLabel(entry.source_url)}
            </a>
          </div>
        )}

        {/* ── Params / Settings ── */}
        {entry.params && (
          <div className="detail-section">
            <div className="detail-label">Parameters / Settings</div>
            <div className="detail-value mono" style={{ fontSize: 12, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {entry.params}
            </div>
          </div>
        )}

        {/* ── Prompt / SOP / Summary ── */}
        {loadingFull && !promptFull && (
          <div style={{ padding:'12px 16px', background:'var(--surface2)', borderRadius:8, marginBottom:8 }}>
            <div style={{ height:12, background:'var(--border2)', borderRadius:4, width:'80%', marginBottom:6 }} className="skeleton" />
            <div style={{ height:12, background:'var(--border2)', borderRadius:4, width:'60%', marginBottom:6 }} className="skeleton" />
            <div style={{ height:12, background:'var(--border2)', borderRadius:4, width:'70%' }} className="skeleton" />
          </div>
        )}
        {(promptFull || !['video', 'image'].includes(entry.type)) && (
          <div className="detail-section">
            <div className="detail-label">
              {entry.type === 'distilled' ? 'Summary'
                : entry.type === 'sop' ? 'Standard Operating Procedure'
                : 'Prompt'}
            </div>
            {canSeePrompt ? (
              promptFull ? (
                entry.type === 'sop' ? (
                  <div style={{
                    background: 'var(--surface2)', border: '0.5px solid var(--border2)',
                    borderRadius: 'var(--radius-sm)', padding: '14px 16px',
                    fontSize: 13, lineHeight: 1.7, color: 'var(--text)',
                    maxHeight: promptExpanded ? 'none' : 320,
                    overflow: promptExpanded ? 'visible' : 'hidden',
                    position: 'relative',
                  }}>
                    <ReactMarkdown components={{
                      h1: ({children}) => <h1 style={{fontSize:16,fontWeight:700,marginBottom:8,marginTop:0,color:'var(--text)'}}>{children}</h1>,
                      h2: ({children}) => <h2 style={{fontSize:14,fontWeight:600,marginBottom:6,marginTop:14,color:'var(--text)',borderBottom:'0.5px solid var(--border)',paddingBottom:4}}>{children}</h2>,
                      h3: ({children}) => <h3 style={{fontSize:13,fontWeight:600,marginBottom:4,marginTop:10,color:'var(--accent)'}}>{children}</h3>,
                      p:  ({children}) => <p  style={{margin:'0 0 8px',color:'var(--text)'}}>{children}</p>,
                      li: ({children}) => <li style={{marginBottom:3,color:'var(--text)'}}>{children}</li>,
                      ul: ({children}) => <ul style={{paddingLeft:18,marginBottom:8}}>{children}</ul>,
                      ol: ({children}) => <ol style={{paddingLeft:18,marginBottom:8}}>{children}</ol>,
                      strong: ({children}) => <strong style={{color:'var(--text)',fontWeight:600}}>{children}</strong>,
                      code: ({children}) => <code style={{background:'var(--surface)',padding:'1px 5px',borderRadius:3,fontSize:11,fontFamily:'var(--mono)',color:'var(--accent)'}}>{children}</code>,
                    }}>
                      {promptFull}
                    </ReactMarkdown>
                    {!promptExpanded && promptFull.length > 400 && (
                      <div style={{position:'absolute',bottom:0,left:0,right:0,height:40,background:'linear-gradient(transparent,var(--surface2))'}} />
                    )}
                  </div>
                ) : (
                  <div>
                    <div className="prompt-display">{promptShown}</div>
                    {needsTruncate && (
                      <button onClick={() => setPromptExpanded(e => !e)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--accent)', fontFamily: 'var(--font)',
                          fontSize: 12, padding: '6px 0 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                        {promptExpanded ? '▲ Show less' : '▼ Show full prompt'}
                      </button>
                    )}
                  </div>
                )
              ) : (
                <div className="detail-value" style={{ color: 'var(--dim)', fontSize: 12.5, fontStyle: 'italic' }}>
                  No content recorded
                </div>
              )
            ) : (
              <div style={{ position: 'relative' }}>
                <div className="prompt-display" style={{ filter: 'blur(4px)', userSelect: 'none', pointerEvents: 'none', opacity: 0.6 }}>
                  {promptFull.slice(0, 120) || 'Sign in to view'}
                </div>
                <div style={{ position: 'absolute', inset: 0, display: 'flex',
                  flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  gap: 8, background: 'rgba(14,14,15,0.75)', borderRadius: 'var(--radius-sm)' }}>
                  <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Sign in to view full content</span>
                  <Link to="/auth" style={{ padding: '6px 14px', background: 'var(--accent)', color: '#fff',
                    borderRadius: 'var(--radius-sm)', fontSize: 12, textDecoration: 'none', fontWeight: 500 }}>Sign in</Link>
                </div>
              </div>
            )}
            {entry.type === 'sop' && promptFull.length > 400 && (
              <button onClick={() => setPromptExpanded(e => !e)}
                style={{ background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--accent)', fontFamily: 'var(--font)',
                  fontSize: 12, padding: '6px 0 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                {promptExpanded ? '▲ Show less' : '▼ Show full SOP'}
              </button>
            )}
          </div>
        )}

        {/* ── Text / code / longform output ── */}
        {fullEntry.text_output && (
          <div className="detail-section">
            <div className="detail-label">
              {entry.type === 'code' ? 'Code output'
                : entry.type === 'longform' ? 'Long form output'
                : entry.type === 'prompt' ? 'Prompt output'
                : 'Output'}
            </div>
            {canSeePrompt ? (
              <div className="prompt-display" style={{
                color: 'var(--text)',
                fontFamily: entry.type === 'code' ? 'var(--mono)' : undefined,
                fontSize: entry.type === 'code' ? 12 : undefined,
                whiteSpace: 'pre-wrap', maxHeight: 320, overflowY: 'auto',
              }}>
                {fullEntry.text_output}
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <div className="prompt-display" style={{
                  filter: 'blur(4px)', userSelect: 'none', pointerEvents: 'none', opacity: 0.5,
                  maxHeight: 80, overflow: 'hidden',
                }}>
                  {fullEntry.text_output.slice(0, 120)}
                </div>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'rgba(14,14,15,0.6)', borderRadius: 'var(--radius-sm)' }}>
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>Sign in to view output</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Notes ── */}
        {canSeePrompt && fullEntry.notes && (
          <div className="detail-section">
            <div className="detail-label">Notes</div>
            <div className="detail-value" style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.7 }}>
              {fullEntry.notes}
            </div>
          </div>
        )}

        {/* ── Tags ── */}
        {entry.tags?.length > 0 && (
          <div className="detail-section">
            <div className="detail-label">Tags</div>
            <div className="entry-meta" style={{ flexWrap: 'wrap', gap: 5 }}>
              {entry.tags.map(t => <span key={t} className="tag tag-custom">{t}</span>)}
            </div>
          </div>
        )}

        {/* ── File attachment ── */}
        {entry.file_name && !['image', 'video'].includes(entry.type) && (
          <div className="detail-section">
            <div className="detail-label">Attached file</div>
            <div className="detail-value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16 }}>📎</span>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>{entry.file_name}</span>
            </div>
          </div>
        )}

        {/* ── Meta ── */}
        <div className="detail-section">
          <div className="detail-label">Submitted by</div>
          <div className="detail-value" style={{ fontSize: 12.5, color: 'var(--muted)' }}>
            {entry.profiles?.username || 'anonymous'}
          </div>
        </div>

        <div className="detail-section">
          <div className="detail-label">Uploaded</div>
          <div className="detail-value" style={{ fontSize: 12.5, color: 'var(--muted)' }}>
            {formatFullDate(entry.created_at)}
          </div>
        </div>

        <div className="detail-divider" />

        {/* ── Share to team ── */}
        {canShareToTeam && (
          <div style={{ padding: '0 0 4px' }}>
            <ShareToTeam
              entry={entry}
              onShared={(teamId, pending) => {
                setShowShareToast(pending ? 'Submitted for approval ⏳' : 'Shared to team ✓');
                setTimeout(() => setShowShareToast(''), 2500);
              }}
            />
            {showShareToast && (
              <div style={{
                marginTop: 6, fontSize: 12,
                color: showShareToast.includes('approval') ? '#ef9f27' : '#1d9e75',
                background: showShareToast.includes('approval') ? 'rgba(239,159,39,0.1)' : 'rgba(29,158,117,0.1)',
                border: `0.5px solid ${showShareToast.includes('approval') ? 'rgba(239,159,39,0.3)' : 'rgba(29,158,117,0.3)'}`,
                borderRadius: 6, padding: '5px 10px',
              }}>
                {showShareToast}
              </div>
            )}
          </div>
        )}

        {/* ── Copy + Bookmark + Share + Report ── */}
        {canSeePrompt && (
          <div className="detail-actions" style={{ position: 'relative', flexWrap: 'wrap', gap: 6 }}>

            {/* Copy prompt */}
            {promptFull && (
              <button className={`btn-copy ${copied ? 'copied' : ''}`} onClick={copyPrompt}>
                {copied ? '✓ Copied!' : entry.type === 'sop' ? 'Copy SOP' : 'Copy prompt'}
              </button>
            )}

            {/* Bookmark — non-owners only */}
            {canInteract && (
              <button
                onClick={handleBookmark}
                disabled={bookmarking}
                style={{
                  background: isBookmarked ? 'rgba(245,158,11,0.1)' : 'var(--surface2)',
                  border: `0.5px solid ${isBookmarked ? 'rgba(245,158,11,0.4)' : 'var(--border2)'}`,
                  borderRadius: 'var(--radius-sm)', padding: '9px 12px',
                  cursor: bookmarking ? 'wait' : 'pointer',
                  color: isBookmarked ? '#f59e0b' : 'var(--muted)',
                  fontFamily: 'var(--font)', fontSize: 12,
                  display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.15s',
                }}>
                <svg width="13" height="13" viewBox="0 0 24 24"
                  fill={isBookmarked ? '#f59e0b' : 'none'}
                  stroke={isBookmarked ? '#f59e0b' : 'currentColor'} strokeWidth="2">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
                {isBookmarked ? 'Saved' : 'Bookmark'}
              </button>
            )}

            {/* Share */}
            <div style={{ position: 'relative' }}>
              <button onClick={() => setShowShareMenu(m => !m)} title="Share this entry"
                style={{
                  background: shareCopied ? 'rgba(29,158,117,0.12)' : 'var(--surface2)',
                  border: `0.5px solid ${shareCopied ? 'rgba(29,158,117,0.4)' : 'var(--border2)'}`,
                  borderRadius: 'var(--radius-sm)', padding: '9px 12px',
                  cursor: 'pointer', color: shareCopied ? '#5dcaa5' : 'var(--muted)',
                  fontFamily: 'var(--font)', fontSize: 12, display: 'flex',
                  alignItems: 'center', gap: 5, transition: 'all 0.12s',
                }}>
                {shareCopied ? '✓' : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                    <polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/>
                  </svg>
                )}
                {shareCopied ? 'Link copied!' : 'Share'}
              </button>
              {showShareMenu && (
                <div style={{
                  position: 'absolute', bottom: '100%', right: 0, marginBottom: 6,
                  background: 'var(--surface)', border: '0.5px solid var(--border2)',
                  borderRadius: 'var(--radius)', zIndex: 20, minWidth: 180,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.2)', overflow: 'hidden',
                }}>
                  <div onClick={shareEntry} style={shareItemStyle}><span>🔗</span> Copy link</div>
                  {typeof navigator !== 'undefined' && navigator.share && (
                    <div onClick={shareNative} style={shareItemStyle}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                      </svg>
                      Share via...
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Report */}
            {user && !isOwner && !isAdmin && (
              <button onClick={() => setShowReport(true)}
                style={{
                  background: 'none', border: '0.5px solid var(--border2)',
                  borderRadius: 'var(--radius-sm)', padding: '9px 12px',
                  fontFamily: 'var(--font)', fontSize: 12, color: 'var(--dim)',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, transition: 'color 0.12s',
                }}
                title="Report this entry"
                onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--dim)'}>
                ⚑ Report
              </button>
            )}
          </div>
        )}

        {/* Admin report badge */}
        {isAdmin && entry.report_count > 0 && (
          <div style={{
            fontSize: 12, color: 'var(--danger)', background: 'rgba(226,75,74,0.08)',
            border: '0.5px solid rgba(226,75,74,0.25)', borderRadius: 'var(--radius-sm)',
            padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 6,
          }}>
            ⚑ {entry.report_count} report{entry.report_count !== 1 ? 's' : ''}
            {entry.is_flagged && <span style={{ color: 'var(--danger)', fontWeight: 500 }}> · Auto-hidden</span>}
          </div>
        )}

        {/* ── Owner/admin controls ── */}
        {canEdit && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
            {folders && folders.length > 0 && (
              <div style={{ position: 'relative' }}>
                <button onClick={() => setMovingFolder(m => !m)} style={{
                  width: '100%', background: 'var(--surface2)', border: '0.5px solid var(--border2)',
                  borderRadius: 'var(--radius-sm)', padding: '9px', fontFamily: 'var(--font)',
                  fontSize: 12.5, color: 'var(--muted)', cursor: 'pointer', textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  {entry.folders?.name ? (
                    <>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: entry.folders.color, display: 'inline-block' }} />
                      {entry.folders.name}
                    </>
                  ) : '◈ Move to folder'}
                  <span style={{ marginLeft: 'auto' }}>▾</span>
                </button>
                {movingFolder && (
                  <div style={{
                    position: 'absolute', bottom: '100%', left: 0, right: 0, marginBottom: 4,
                    background: 'var(--surface)', border: '0.5px solid var(--border2)',
                    borderRadius: 'var(--radius)', zIndex: 20, overflow: 'hidden',
                  }}>
                    <div onClick={() => handleMoveFolder(null)} style={{
                      padding: '9px 14px', fontSize: 13, color: 'var(--muted)',
                      cursor: 'pointer', borderBottom: '0.5px solid var(--border)',
                    }}>◈ Unfiled</div>
                    {folders.map(f => (
                      <div key={f.id} onClick={() => handleMoveFolder(f.id)} style={{
                        padding: '9px 14px', fontSize: 13, color: 'var(--text)',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                        borderBottom: '0.5px solid var(--border)',
                      }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: f.color, display: 'inline-block' }} />
                        {f.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <button onClick={handleTogglePublic} disabled={toggling} style={{
              width: '100%', background: 'var(--surface2)', border: '0.5px solid var(--border2)',
              borderRadius: 'var(--radius-sm)', padding: '9px', fontFamily: 'var(--font)',
              fontSize: 12.5, color: 'var(--muted)', cursor: 'pointer',
            }}>
              {entry.is_public ? '🔒 Make private' : '🌐 Make public'}
            </button>
            {/* ── Save as Skill ── */}
            {entry.prompt && (
              <button onClick={() => setShowSaveAsSkill(true)} style={{
                width: '100%', background: 'var(--accent-dim)', border: '0.5px solid var(--accent-border)',
                borderRadius: 'var(--radius-sm)', padding: '9px', fontFamily: 'var(--font)',
                fontSize: 12.5, color: 'var(--accent)', cursor: 'pointer', fontWeight: 500,
              }}>
                ⚡ Save as Skill
              </button>
            )}
            <button className="btn-delete" onClick={handleDelete}>Delete entry</button>
          </div>
        )}
      </div>

      {showReport && (
        <ReportModal entry={entry} onClose={() => setShowReport(false)} onReported={() => setShowReport(false)} />
      )}
      {showSaveAsSkill && (
        <SaveAsSkillModal entry={entry} onClose={() => setShowSaveAsSkill(false)} />
      )}
    </div>
  );
}

// ── Save as Skill modal ─
// Style constants — must be defined before the component that uses them
const lbl = { fontSize: 11, color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 5, fontWeight: 600 };
const inp = { width: '100%', background: 'var(--bg)', border: '0.5px solid var(--border2)', borderRadius: 8, padding: '9px 12px', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 13, outline: 'none', boxSizing: 'border-box' };

// ── Save as Skill modal ────────────────────────────────────────────────────
// Colour palette for variable chips — mirrors SkillsPage
const VAR_PALETTE = [
  { bg: 'rgba(127,119,221,0.15)', border: 'rgba(127,119,221,0.5)', text: '#afa9ec' },
  { bg: 'rgba(29,158,117,0.15)',  border: 'rgba(29,158,117,0.5)',  text: '#5dcaa5' },
  { bg: 'rgba(239,159,39,0.15)',  border: 'rgba(239,159,39,0.5)',  text: '#ef9f27' },
  { bg: 'rgba(133,183,235,0.15)', border: 'rgba(133,183,235,0.5)', text: '#85b7eb' },
  { bg: 'rgba(226,75,74,0.15)',   border: 'rgba(226,75,74,0.5)',   text: '#f09595' },
];
const varPalette = i => VAR_PALETTE[i % VAR_PALETTE.length];

function parseVars(prompt) {
  const matches = [...(prompt || '').matchAll(/\{\{([^}]+)\}\}/g)];
  const seen = new Set();
  return matches.map(m => m[1].trim()).filter(v => { if (seen.has(v)) return false; seen.add(v); return true; });
}

function SaveAsSkillModal({ entry, onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [title,    setTitle]    = useState(entry.title || '');
  const [prompt,   setPrompt]   = useState(entry.prompt || '');
  const [desc,     setDesc]     = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [done,     setDone]     = useState(false);

  const detectedVars = parseVars(prompt);

  const handleSave = async () => {
    if (!title.trim()) { alert('Please add a skill name'); return; }
    if (!prompt.trim()) { alert('Prompt cannot be empty'); return; }
    setSaving(true);
    try {
      const { insertSkill } = await import('../lib/supabase');
      await insertSkill({
        user_id:     user.id,
        title:       title.trim(),
        description: desc.trim() || null,
        prompt:      prompt.trim(),
        model:       entry.model || null,
        type:        entry.type  || 'text',
        variables:   detectedVars.map(name => ({ name, label: name, placeholder: '' })),
        is_public:   isPublic,
        tags:        entry.tags || [],
        use_count:   0,
      });
      setDone(true);
      setTimeout(() => { onClose(); navigate('/skills'); }, 1600);
    } catch (e) { alert('Failed: ' + e.message); }
    setSaving(false);
  };

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        background: 'var(--surface)', borderRadius: 16, width: '100%', maxWidth: 520,
        maxHeight: '92dvh', display: 'flex', flexDirection: 'column',
        border: '0.5px solid var(--border2)', overflow: 'hidden',
      }}>

        {/* Header */}
        <div style={{ padding: '18px 22px 14px', borderBottom: '0.5px solid var(--border)', flexShrink: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>⚡ Save as Skill</div>
          <div style={{ fontSize: 12, color: 'var(--dim)', lineHeight: 1.5 }}>
            Edit the prompt and wrap any part you want to customise in{' '}
            <code style={{ background: 'var(--surface2)', padding: '1px 5px', borderRadius: 4, fontSize: 11, color: 'var(--accent)', fontFamily: 'var(--mono)' }}>{'{{variable_name}}'}</code>{' '}
            to make it reusable.
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Skill name */}
          <div>
            <label style={lbl}>Skill name *</label>
            <input value={title} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. LinkedIn Post Generator"
              style={inp} />
          </div>

          {/* Prompt editor */}
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 5 }}>
              <label style={lbl}>Prompt template *</label>
              <span style={{ fontSize: 10, color: 'var(--dim)' }}>
                Wrap parts in {'{{variable}}'} to make them editable
              </span>
            </div>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              style={{ ...inp, minHeight: 160, resize: 'vertical', fontFamily: 'var(--mono)', fontSize: 12, lineHeight: 1.65 }}
            />
            {/* Detected variables */}
            {detectedVars.length > 0 && (
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: 'var(--dim)' }}>Variables detected:</span>
                {detectedVars.map((v, i) => {
                  const c = varPalette(i);
                  return (
                    <span key={v} style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 6,
                      background: c.bg, border: `0.5px solid ${c.border}`,
                      color: c.text, fontFamily: 'var(--mono)',
                    }}>
                      {`{{${v}}}`}
                    </span>
                  );
                })}
              </div>
            )}
            {detectedVars.length === 0 && prompt.trim() && (
              <div style={{ marginTop: 6, fontSize: 11, color: 'var(--dim)', lineHeight: 1.5 }}>
                💡 Tip: replace specific words like a topic or tone with{' '}
                <code style={{ fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{'{{topic}}'}</code> or{' '}
                <code style={{ fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{'{{tone}}'}</code>{' '}
                to make this prompt reusable for any situation.
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label style={lbl}>Description <span style={{ textTransform: 'none', fontWeight: 400, fontSize: 10 }}>(optional)</span></label>
            <textarea value={desc} onChange={e => setDesc(e.target.value)}
              placeholder="What does this skill do? Who is it for?"
              style={{ ...inp, minHeight: 64, resize: 'vertical', lineHeight: 1.6 }} />
          </div>

          {/* Visibility */}
          <div>
            <label style={lbl}>Visibility</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[{ v: false, label: '🔒 Private — just me' }, { v: true, label: '🌐 Public — Marketplace' }].map(({ v, label }) => (
                <button key={String(v)} onClick={() => setIsPublic(v)} style={{
                  flex: 1, padding: '9px', borderRadius: 8, cursor: 'pointer',
                  fontFamily: 'var(--font)', fontSize: 12, fontWeight: isPublic === v ? 600 : 400,
                  background: isPublic === v ? 'var(--accent-dim)' : 'var(--surface2)',
                  border: `0.5px solid ${isPublic === v ? 'var(--accent-border)' : 'var(--border2)'}`,
                  color: isPublic === v ? 'var(--accent)' : 'var(--muted)',
                  textAlign: 'center',
                }}>{label}</button>
              ))}
            </div>
            {isPublic && (
              <div style={{ marginTop: 8, fontSize: 11, color: 'var(--accent)', background: 'var(--accent-dim)', border: '0.5px solid var(--accent-border)', borderRadius: 8, padding: '8px 12px', lineHeight: 1.6 }}>
                🚀 Appears in the Marketplace immediately. Add a cover image and tags after saving to boost discoverability.
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 22px', borderTop: '0.5px solid var(--border)', display: 'flex', gap: 8, flexShrink: 0 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '11px', background: 'none', border: '0.5px solid var(--border2)', borderRadius: 8, fontFamily: 'var(--font)', fontSize: 13, color: 'var(--muted)', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || done} style={{
            flex: 2, padding: '11px', borderRadius: 8,
            fontFamily: 'var(--font)', fontSize: 13, fontWeight: 600,
            cursor: saving ? 'wait' : 'pointer',
            background: done ? 'rgba(29,158,117,0.15)' : 'var(--accent)',
            color: done ? '#1d9e75' : '#fff',
            border: done ? '0.5px solid rgba(29,158,117,0.4)' : 'none',
            transition: 'all 0.2s',
          }}>
            {done ? '✓ Saved — going to My Skills…' : saving ? 'Saving…' : `⚡ Save as Skill${detectedVars.length > 0 ? ` (${detectedVars.length} var${detectedVars.length !== 1 ? 's' : ''})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
